<template>
  <VContainer fluid class="m-0 pl-6 pr-6 w-full">
    <VRow class="flex-nowrap align-center xl:gap-4" no-gutters>
      <VCol class="flex-grow-0">
        <VImg
          data-testid="menu-icon"
          :width="35"
          :height="40"
          :src="logo"
          alt="Telehealth Logo"
          draggable="false"
          class="cursor-pointer"
          @click="routeToConsultations"
        />
      </VCol>
      <VCol v-if="isConnectEnabled" class="flex-grow-0 max-xl:hidden">
        <VBtn
          data-testid="menu-consultations-button"
          :to="{ name: routeNames.CONSULTATIONS }"
          :active="false"
          class="px-4 py-1 font-semibold"
          :class="{
            'text-primary bg-[#F5F5F7]': isConsultationsActive,
            'text-secondary-text': !isConsultationsActive
          }"
        >
          <div class="flex gap-2 items-center">
            <VIcon icon="calendar_month" size="24px" />
            {{ t('Consultations') }}
          </div>
        </VBtn>
      </VCol>

      <VCol
        v-if="canListPatients"
        data-testid="menu-patient-monitoring-button"
        class="flex-grow-0 max-xl:hidden"
      >
        <VBtn
          :to="{ name: routeNames.VITAL_DATA }"
          :active="false"
          class="button-large mr-4"
          :class="{
            'text-primary bg-[#F5F5F7]': isVitalDataActive,
            'text-secondary-text': !isVitalDataActive
          }"
        >
          <VIcon icon="custom:patientList" size="24px" />
          {{ t('Patient Overview') }}
        </VBtn>
      </VCol>

      <VCol v-if="canListPatients" class="max-xl:hidden" cols="3">
        <PatientSearch />
      </VCol>

      <VSpacer />

      <VCol v-if="isConnectEnabled && showShiftIndicator" class="flex-grow-0 align-center">
        <ShiftStatusIndicator />
      </VCol>

      <VCol
        v-if="canCreateConsultations && isConnectEnabled"
        data-testid="menu-emergency-button"
        class="flex-grow-0"
        :class="{ hidden: isMobileMenuDrawerOpened }"
      >
        <VTooltip location="bottom" :disabled="isEmergencyRoutingAvailable">
          {{ t('No physician available in the Intensive Care Unit to accept emergency calls.') }}
          <template #activator="activator">
            <span v-bind="activator.props">
              <VBtn
                :active="false"
                class="button-large"
                color="secondary"
                :disabled="!isEmergencyRoutingAvailable"
                @click="openEmergencyModal()"
              >
                <template #prepend>
                  <VIcon icon="emergency" class="material-icons-round" />
                </template>
                {{ t('Emergency Call') }}
              </VBtn>
            </span>
          </template>
        </VTooltip>
      </VCol>

      <VCol v-if="isConnectEnabled" class="flex-grow-0">
        <VBtn
          :active="false"
          data-testid="btn-notification-bell"
          :disabled="hasGodModeShift"
          @click="routeToNotificationCenter"
        >
          <template v-if="newNotificationsAvailable">
            <VBadge dot color="success-badge">
              <VIcon icon="notifications" size="32px" class="text-secondary-text" />
            </VBadge>
          </template>
          <template v-else>
            <VIcon icon="notifications" size="32px" class="text-secondary-text" />
          </template>
        </VBtn>
      </VCol>
      <VCol class="flex-grow-0 max-xl:hidden" data-testid="user-menu">
        <UserMenu />
      </VCol>
      <VCol class="flex-grow-0 xl:hidden">
        <v-app-bar-nav-icon
          :icon="isMobileMenuDrawerOpened ? '$close' : '$menu'"
          variant="text"
          @click.stop="toggleMobileMenuDrawer"
        />
      </VCol>
    </VRow>
  </VContainer>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import logo from '@/assets/tcclogo.png'
import UserMenu from '@/components/navigation/UserMenu.vue'
import ShiftStatusIndicator from '@/components/navigation/ShiftStatusIndicator.vue'
import { routeNames } from '@/router/routeNames'
import { computed, inject } from 'vue'
import { storeToRefs } from 'pinia'
import PatientSearch from '@/components/navigation/patientsearch/PatientSearch.vue'
import { useRouter } from 'vue-router'
import { useDynamicPolling } from '@/composables/useDynamicPolling'
import { useNotificationStore } from '@/stores/notification'
import { getRefreshIntervalNotificationCenter } from '@/global/environmentGetters'
import { useUserStore } from '@/stores/user'
import type { OpenEmergencyModalFn } from '@/types/OpenEmergencyModalFn'
import useRoutingAvailability from '@/composables/useRoutingAvailability'
import { usePluginsStore } from '@/stores/plugins'

interface Props {
  showShiftIndicator: boolean
  modelValue: boolean
}

const { t } = useI18n()
const router = useRouter()
defineProps<Props>()
const { isEmergencyRoutingAvailable } = useRoutingAvailability()

const openEmergencyModal = inject<OpenEmergencyModalFn>('openEmergencyModal', () => {})
const isMobileMenuDrawerOpened = defineModel<boolean>()

const { hasGodModeShift, canListPatients, canCreateConsultations, isIncognito } =
  storeToRefs(useUserStore())
const { isConnectEnabled } = storeToRefs(usePluginsStore())

const isVitalDataActive = computed(() => router.currentRoute.value.name === routeNames.VITAL_DATA)
const isConsultationsActive = computed(
  () => router.currentRoute.value.name === routeNames.CONSULTATIONS
)

const { newNotificationsAvailable } = storeToRefs(useNotificationStore())

useDynamicPolling(
  useNotificationStore().refreshNotificationList,
  getRefreshIntervalNotificationCenter(),
  isIncognito
)

function toggleMobileMenuDrawer() {
  isMobileMenuDrawerOpened.value = !isMobileMenuDrawerOpened.value
}

async function routeToConsultations() {
  await router.push({ name: routeNames.CONSULTATIONS })
}

async function routeToNotificationCenter() {
  await router.push({ name: routeNames.NOTIFICATION_CENTER })
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
